import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemHealthPanel = () => {
  const systemMetrics = [
    {
      name: "Server Uptime",
      value: "99.98%",
      status: "healthy",
      icon: "Server",
      description: "Last 30 days average",
      color: "green"
    },
    {
      name: "API Response Time",
      value: "145ms",
      status: "healthy",
      icon: "Zap",
      description: "Average response time",
      color: "green"
    },
    {
      name: "Database Performance",
      value: "Optimal",
      status: "healthy",
      icon: "Database",
      description: "Query execution time: 23ms",
      color: "green"
    },
    {
      name: "Storage Usage",
      value: "67%",
      status: "warning",
      icon: "HardDrive",
      description: "2.1 TB of 3.2 TB used",
      color: "yellow"
    },
    {
      name: "Active Connections",
      value: "1,089",
      status: "healthy",
      icon: "Users",
      description: "Current active users",
      color: "green"
    },
    {
      name: "Error Rate",
      value: "0.02%",
      status: "healthy",
      icon: "AlertTriangle",
      description: "Last 24 hours",
      color: "green"
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: "booking",
      message: "New booking created: SLB2025267",
      timestamp: "2 minutes ago",
      icon: "Package",
      color: "blue"
    },
    {
      id: 2,
      type: "payment",
      message: "Payment processed: ₹2,45,000",
      timestamp: "5 minutes ago",
      icon: "DollarSign",
      color: "green"
    },
    {
      id: 3,
      type: "user",
      message: "New user registered: Vikram Reddy",
      timestamp: "12 minutes ago",
      icon: "UserPlus",
      color: "purple"
    },
    {
      id: 4,
      type: "kyc",
      message: "KYC verification completed: USR2025001",
      timestamp: "18 minutes ago",
      icon: "UserCheck",
      color: "green"
    },
    {
      id: 5,
      type: "delivery",
      message: "Delivery completed: SLB2025265",
      timestamp: "25 minutes ago",
      icon: "CheckCircle",
      color: "green"
    }
  ];

  const systemAlerts = [
    {
      id: 1,
      severity: "warning",
      message: "Storage usage approaching 70% threshold",
      timestamp: "1 hour ago",
      action: "Review and archive old data"
    },
    {
      id: 2,
      severity: "info",
      message: "Scheduled maintenance on 2025-12-05 at 02:00 AM",
      timestamp: "3 hours ago",
      action: "Notify users in advance"
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getMetricColor = (color) => {
    switch(color) {
      case 'green': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'yellow': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'red': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default: return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  const getActivityColor = (color) => {
    switch(color) {
      case 'blue': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'green': return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'purple': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'info': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">System Health</h2>
              <p className="text-sm text-muted-foreground">Real-time platform monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {systemMetrics?.map((metric, index) => (
            <div key={index} className={`rounded-lg p-4 border ${getMetricColor(metric?.color)}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${getActivityColor(metric?.color)} flex items-center justify-center`}>
                  <Icon name={metric?.icon} size={20} />
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(metric?.status)}`}>
                  {metric?.status?.toUpperCase()}
                </span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">{metric?.name}</h3>
              <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
              <p className="text-xs text-muted-foreground">{metric?.description}</p>
            </div>
          ))}
        </div>

        {systemAlerts?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">System Alerts</h3>
            <div className="space-y-3">
              {systemAlerts?.map((alert) => (
                <div key={alert?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                            {alert?.severity?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">{alert?.timestamp}</span>
                        </div>
                        <p className="text-sm text-foreground mb-1">{alert?.message}</p>
                        <p className="text-xs text-muted-foreground">Action: {alert?.action}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Recent Activity</h2>
              <p className="text-sm text-muted-foreground">Live platform events</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {recentActivities?.map((activity) => (
            <div key={activity?.id} className="flex items-start gap-4 p-3 border border-border rounded-lg hover:border-primary/50 transition-colors">
              <div className={`w-10 h-10 rounded-lg ${getActivityColor(activity?.color)} flex items-center justify-center flex-shrink-0`}>
                <Icon name={activity?.icon} size={20} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground mb-1">{activity?.message}</p>
                <p className="text-xs text-muted-foreground">{activity?.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthPanel;